/**
 * Hashing the strategic layer.
 *
 * `hashWorld` calls this in one line. Everything in `CampaignState` goes in,
 * including the province graph — the graph is built from data at the start of a
 * campaign but is thereafter mutable state, and a "cache" whose contents can
 * change what the simulation does is not a cache. Leaving it out would let a
 * campaign whose roads had been cut hash identical to one whose had not.
 *
 * The chronicle goes in too. It is bounded, it is deterministic, and hashing it
 * means a divergence in *why* something happened shows up as loudly as a
 * divergence in what happened.
 */

import type { Hasher } from '../../core/hash.ts';
import { RESOURCE_KEYS } from '../types.ts';
import { hashTrade } from './tradesave.ts';
import type { CampaignState } from './types.ts';

export function hashCampaign(h: Hasher, c: CampaignState): void {
  h.u32(c.turn);
  h.u32(c.phase);
  /**
   * `playerNation` is deliberately *not* hashed.
   *
   * It answers "which of these crowns is the one reading this screen", and in
   * a room every client answers differently — which is correct, and which
   * made the fingerprint disagree on a realm that was identical in every
   * respect that matters. What must match is which crowns are human at all,
   * because that is what the AI reads.
   */
  h.i32(c.humanNations.length);
  for (const n of c.humanNations) h.i32(n);
  // `capital` is the other half of `playerNation`: which seat on the board is
  // *this* client's. Not hashed, for the same reason and with the same care —
  // anything that acts on a capital must be told which one, never assume.
  h.u32(c.nextArmyId);
  h.u32(c.nextFleetId);
  h.i32(c.winner);
  h.bool(c.over);
  /**
   * The realm's Age.
   *
   * It scales every standing garrison on the board — `ageGarrisonScale` — so a
   * campaign in the Bell and a campaign in the Ember with identical armies on
   * identical ground are not the same campaign, and one of them holds a capital
   * the other loses.
   */
  h.u32(c.age);

  h.u32(c.nations.length);
  for (const n of c.nations) {
    h.u32(n.def);
    for (const k of RESOURCE_KEYS) h.num(n.treasury[k]);
    h.num(n.manpower);
    h.bool(n.alive);
    h.u32(n.atWar.length);
    for (const v of n.atWar) h.i32(v);
    h.u32(n.relations.length);
    for (const v of n.relations) h.num(v);
    // Diplomacy's per-nation half. A crown's faith, and whose it answers to.
    h.u32(n.confession);
    h.i32(n.liege);
    h.u32(n.subject);
    h.u32(n.subjectSince);
    /**
     * The four estates.
     *
     * State in the fullest sense: each standing is path-dependent — it drifts
     * toward a target read off the board and is knocked about by every answer the
     * crown gives to the realm's chronicle — and each owns a live multiplier on
     * the month. Two campaigns whose Hosts stand at −61 and −59 collect a levy
     * and no levy respectively, so leaving these out would call two boards
     * identical right up to the muster that failed on one of them.
     */
    h.u32(n.estates.length);
    for (const v of n.estates) h.num(v);
  }

  h.u32(c.provinces.length);
  for (const p of c.provinces) {
    h.str(p.holdingKind);
    h.str(p.villageType);
    for (const key of RESOURCE_KEYS) h.num(p.villageStock[key] ?? 0);
    h.i32(p.owner);
    h.num(p.garrison);
    h.u32(p.fort);
    h.num(p.unrest);
    h.num(p.development);
    /**
     * The ladder, and the masons on it.
     *
     * A tier is bought over a whole campaign and changes what the province pays,
     * what its garrison caps at and how high its walls may go, so two boards
     * that differ only in a tier are not the same board. The works are hashed
     * for the same reason `MarchWarning.sinceTurn` is: the materials have
     * already left the yard, and a campaign three months into a road must not
     * hash the same as one that has not broken ground.
     */
    h.u32(p.tier);
    h.u32(p.worksTo);
    h.u32(p.worksT);
    h.i32(p.worksBy);
    h.i32(p.siegeBy);
    h.u32(p.siegeT);
    // Who lost this ground last. It is a casus belli, so it is state.
    h.i32(p.claimedBy);
    // Harbour works. They decide where a fleet may be raised and loaded, and
    // what the province earns on the traffic.
    h.u32(p.port);
    // What this holding's own military works are worth to its standing garrison.
    // Nonzero on the player's capital, where it is read off the buildings
    // actually standing in the home world, and it moves the cap and the floor.
    h.num(p.barracks);
    /**
     * Whether there is a town of yours standing here, and what is owed to it.
     *
     * `settled` decides whether this ground has a settlement world at all —
     * which decides whether its economy runs, whether its own development is
     * written back here every time you leave it, and whether an assault on it is
     * fought in a place or resolved as a number. `wreck` is the sack the next
     * arrival has to pay for. Two campaigns that agree on everything else and
     * differ here are two different games from the next month on.
     */
    h.u32(p.settled);
    h.num(p.wreck);
  }

  /**
   * The warehouses.
   *
   * Everything built out of materials is built out of a particular city's yard,
   * so what is in the yards decides what a crown can do next — and where the
   * yards are decides what everything costs. Both go in.
   */
  h.u32(c.cities.length);
  for (const k of c.cities) {
    h.u32(k.at);
    h.i32(k.owner);
    h.u32(k.founded);
    for (const key of RESOURCE_KEYS) h.num(k.stock[key]);
  }

  // The graph. State, not a cache.
  h.u32(c.links.length);
  for (const l of c.links) {
    h.u32(l.a);
    h.u32(l.b);
    h.u32(l.kind);
    h.u32(l.march);
  }

  h.u32(c.armies.length);
  for (const a of c.armies) {
    h.u32(a.id);
    h.u32(a.owner);
    h.i32(a.at);
    h.i32(a.moveTo);
    h.i32(a.moveT);
    h.i32(a.moveFrom);
    h.i32(a.moveTotal);
    h.i32(a.dest);
    h.i32(a.intercepting);
    // Months a host is still forming up in after a fight on the road. It decides
    // whether it may march, chase or be caught this month, so two campaigns that
    // differ only in this are two different campaigns.
    h.u32(a.regroup);
    h.u32(a.stance);
    h.u32(a.siegeTurns);
    h.num(a.supply);
    h.u32(a.victories);
    h.i32(a.objective);
    h.u32(a.idleTurns);
    // Which hull it is in, or -1. A host at sea is off the board entirely, so
    // two campaigns that differ only in this differ in what can be fought.
    h.i32(a.aboard);
    // How long it has stood where it may not. A host three months into an
    // overstay is one month from starving and a host that arrived this month is
    // not, and nothing else on the record can tell the two apart.
    h.u32(a.trespassT);
    // Inside the walls or out in the field. It decides whether this host can be
    // met on the ground it stands on, whether it may be given an order at all,
    // and whether the province behind it can be walked into.
    h.bool(a.sheltered);
    h.str(a.name);
    h.u32(a.heroes.length);
    for (const hero of a.heroes) {
      h.u32(hero.id); h.str(hero.classId); h.str(hero.name);
      h.u32(hero.level); h.num(hero.xp); h.bool(hero.fallen); h.num(hero.reviveT);
      h.i32(hero.unitH); h.i32(hero.companyId); h.i32(hero.armyId);
      const powers = Object.keys(hero.cooldowns).sort();
      h.u32(powers.length);
      for (const power of powers) { h.str(power); h.num(hero.cooldowns[power]); }
    }
    h.u32(a.regiments.length);
    for (const r of a.regiments) {
      h.u32(r.typeIdx);
      h.num(r.models);
    }
  }

  /**
   * The sea: the lane network and everything floating on it.
   *
   * The lanes go in for the same reason `links` does. They are built from the
   * generated realm at the start of a campaign and are mutable state thereafter
   * — nothing closes a lane today, and the moment a blockade does, a hash that
   * had left them out would call a campaign with the Narrows shut identical to
   * one with it open.
   */
  h.u32(c.lanes.length);
  for (const l of c.lanes) {
    h.u32(l.a);
    h.u32(l.b);
    h.num(l.months);
  }

  h.u32(c.fleets.length);
  for (const f of c.fleets) {
    h.u32(f.id);
    h.u32(f.owner);
    h.i32(f.at);
    h.i32(f.moveTo);
    h.i32(f.moveFrom);
    h.i32(f.moveT);
    h.i32(f.moveTotal);
    h.i32(f.intercepting);
    h.u32(f.ships);
    h.str(f.name);
    h.u32(f.cargo.length);
    for (const id of f.cargo) h.u32(id);
  }

  /**
   * Brigands and the warning list.
   *
   * Both are state in the full sense. A camp changes what a province yields and
   * what an army walking into it meets; a warning carries `sinceTurn`, which is
   * how long the player has known, and which no other field can be derived from
   * once a turn has passed. A campaign that had been warned must not hash the
   * same as one that had not.
   */
  h.u32(c.camps.length);
  for (const k of c.camps) {
    h.u32(k.at);
    h.num(k.strength);
    h.u32(k.founded);
    h.u32(k.bounty);
  }

  h.u32(c.warnings.length);
  for (const wn of c.warnings) {
    h.u32(wn.kind);
    h.i32(wn.nation);
    h.i32(wn.armyId);
    h.i32(wn.from);
    h.i32(wn.to);
    h.i32(wn.turns);
    h.num(wn.strength);
    h.u32(wn.sinceTurn);
  }

  /**
   * Diplomacy.
   *
   * All four lists and both counters. The counters matter as much as the lists:
   * two campaigns that reached the same treaties by different routes have issued
   * different numbers of war ids, and the next war either of them declares will
   * be numbered differently. A hash that ignored that would call two boards
   * identical right up until the moment they stopped being.
   */
  const d = c.diplo;
  h.u32(d.nextWarId);
  h.u32(d.nextOfferId);

  h.u32(d.wars.length);
  for (const war of d.wars) {
    h.u32(war.id);
    h.i32(war.attacker);
    h.i32(war.defender);
    h.u32(war.attackers.length);
    for (const n of war.attackers) h.i32(n);
    h.u32(war.defenders.length);
    for (const n of war.defenders) h.i32(n);
    h.u32(war.cb);
    h.i32(war.goal);
    h.u32(war.startTurn);
    h.num(war.score);
    h.u32(war.battlesWon);
    h.u32(war.battlesLost);
    h.u32(war.taken);
    h.u32(war.lost);
    h.i32(war.lastOffer);
  }

  h.u32(d.treaties.length);
  for (const t of d.treaties) {
    h.u32(t.kind);
    h.i32(t.a);
    h.i32(t.b);
    h.u32(t.since);
  }

  h.u32(d.truces.length);
  for (const t of d.truces) {
    h.i32(t.a);
    h.i32(t.b);
    h.u32(t.until);
  }

  /**
   * Rights of passage.
   *
   * Directional, so both ends go in and in that order — a board where the Litany
   * may cross Halethwaite is not the board where Halethwaite may cross the
   * Litany, and hashing an unordered pair would call the two identical. `since`
   * is in for the same reason `Treaty.since` is: it is what the panel prints and
   * what any future clause about an old arrangement would read.
   */
  h.u32(d.access.length);
  for (const g of d.access) {
    h.i32(g.from);
    h.i32(g.to);
    h.u32(g.since);
  }

  h.u32(d.offers.length);
  for (const o of d.offers) {
    h.u32(o.id);
    h.u32(o.kind);
    h.i32(o.from);
    h.i32(o.to);
    h.i32(o.war);
    h.i32(o.term);
    h.i32(o.province);
    h.num(o.gold);
    h.bool(o.fromConcedes);
    h.u32(o.expires);
  }

  /**
   * Trade. The node graph, every node's control and payout, and the merchants.
   *
   * All of it, and the graph as much as the rest: it is grown from a *generated*
   * realm, so two builds whose arrows point different ways are two different
   * boards. Control is the entry that matters most — it is smoothed month over
   * month, so it is path-dependent, and a campaign that took its market last
   * season is being paid differently from one that took it this season while
   * looking identical in every other field. See `tradesave.ts`.
   */
  hashTrade(h, c.trade);

  /**
   * The free market's book, every field of every order.
   *
   * An open sell order is escrow — goods that have left a treasury and exist
   * nowhere but here — so a hash that skipped the book would call two boards
   * identical while one of them is owed forty iron. The counter goes in for
   * the reason `diplo.nextWarId` does: two books that reached the same orders
   * by different routes number the next order differently.
   *
   * `amount` and `price` are integers by construction (units, and tenths of a
   * gold), and both are clamped positive at every write — so there is no
   * rounded signed number here and no negative zero to normalise. If either
   * ever becomes fractional, reread the `-0` warning in `state.ts` first.
   */
  h.u32(c.market.nextOrderId);
  h.u32(c.market.orders.length);
  for (const o of c.market.orders) {
    h.u32(o.id);
    h.i32(o.nation);
    h.u32(o.kind);
    h.str(o.key);
    h.u32(o.amount);
    h.u32(o.price);
    h.u32(o.placedTurn);
  }

  const p = c.pending;
  h.bool(p !== null);
  if (p) {
    h.u32(p.province);
    h.u32(p.kind);
    h.i32(p.roadTo);
    h.i32(p.interceptor);
    h.i32(p.attackerNation);
    h.i32(p.defenderNation);
    h.i32(p.attackerArmy);
    h.i32(p.defenderArmy);
    h.num(p.attackerPoints);
    h.num(p.defenderPoints);
    h.str(p.scenario);
    h.bool(p.playerFights);
    h.bool(p.playerAttacks);
    h.u32(p.seed);
  }

  /**
   * The realm's chronicle: the question on the table and every answer ever given.
   *
   * The pending question is state because the realm is *frozen* while it is set —
   * `stepRealm` refuses to start the next month — so whether it is set is part of
   * what "this campaign" means. Its frozen context goes in with it: two campaigns
   * asked the same question about different provinces are two different
   * campaigns, and the context is what the answer will be applied to.
   *
   * The history goes in for the reason `w.eventHistory` does in the settlement:
   * it is what makes a save remember which answers were already spent, and it is
   * the only record of *why* a crown's estates stand where they do. A divergence
   * in the reason shows up as loudly as a divergence in the result.
   *
   * The RNG cursor needs no entry here — every draw comes off `w.rngCampaign`,
   * whose `s` is already hashed by `hashWorld`.
   */
  const pe = c.pendingEvent;
  h.bool(pe !== null);
  if (pe) {
    h.str(pe.id);
    h.u32(pe.turn);
    h.i32(pe.ctx.nation);
    h.i32(pe.ctx.province);
    h.i32(pe.ctx.other);
    h.i32(pe.ctx.army);
    h.i32(pe.ctx.region);
    h.u32(pe.choices.length);
    for (const id of pe.choices) h.str(id);
  }

  h.u32(c.eventHistory.length);
  for (const e of c.eventHistory) h.str(e);

  h.u32(c.log.length);
  for (const line of c.log) h.str(line);
}
